if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        this.errorHtml = this.querySelector('template').content.firstElementChild.cloneNode(true);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        this.onClickShowDrawer = this.onClickShowDrawer.bind(this);
        this.requestId = 0;
        this.fetchAvailability(this.dataset.variantId);
      }

      clearDrawer() {
        const drawer = document.querySelector('pickup-availability-drawer');
        if (drawer?.hasAttribute('open')) drawer.hide();
        drawer?.remove();
      }

      setAlternateButton(loading = false) {
        const button = document.querySelector('#ShowPickupAvailabilityDrawerAlternate');
        if (!button) return;
        button.disabled = loading;
        button.setAttribute('aria-busy', String(loading));
        button.removeEventListener('click', this.onClickShowDrawer);
        button.addEventListener('click', this.onClickShowDrawer);
      }

      onClickShowDrawer(event) {
        const drawer = document.querySelector('pickup-availability-drawer');
        if (drawer) drawer.show(event.currentTarget);
        else this.onClickRefreshList();
      }

      fetchAvailability(variantId) {
        if (!variantId) return;

        this.dataset.variantId = variantId;
        const requestId = ++this.requestId;
        // Clear the previous variant before loading, including failed requests.
        this.clearDrawer();
        this.setAlternateButton(true);
        let rootUrl = this.dataset.rootUrl;
        if (!rootUrl.endsWith('/')) rootUrl += '/';
        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

        return fetch(variantSectionUrl)
          .then((response) => {
            if (!response.ok) throw new Error('Pickup availability request failed');
            return response.text();
          })
          .then((text) => {
            if (requestId !== this.requestId) return;
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            const drawer = this.renderPreview(sectionInnerHTML);
            if (drawer) this.fetchLocationInventory(variantId, drawer, requestId);
          })
          .catch(() => {
            if (requestId === this.requestId) this.renderError();
          })
          .finally(() => {
            if (requestId === this.requestId) this.setAlternateButton();
          });
      }

      onClickRefreshList() {
        return this.fetchAvailability(this.dataset.variantId);
      }

      async fetchLocationInventory(variantId, drawer, requestId) {
        const token = this.dataset.storefrontToken;
        const domain = this.dataset.storefrontDomain;
        if (!token || !domain || !drawer.querySelector('[data-location-inventory]')) return;

        const query = `query PickupInventory($id: ID!, $after: String) {
          node(id: $id) {
            ... on ProductVariant {
              storeAvailability(first: 100, after: $after) {
                nodes { available quantityAvailable location { id } }
                pageInfo { hasNextPage endCursor }
              }
            }
          }
        }`;

        try {
          let after = null;
          do {
            const response = await fetch(`https://${domain}/api/2026-10/graphql.json`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': token,
              },
              body: JSON.stringify({
                query,
                variables: { id: `gid://shopify/ProductVariant/${variantId}`, after },
              }),
            });
            if (!response.ok) throw new Error('Inventory request failed');
            const result = await response.json();
            if (requestId !== this.requestId || !drawer.isConnected) return;
            const availability = result.data?.node?.storeAvailability;
            if (result.errors?.length || !availability) throw new Error('Inventory data unavailable');

            availability.nodes.forEach((store) => {
              if (!Number.isInteger(store.quantityAvailable)) return;
              const locationId = store.location.id.split('/').pop();
              const label = drawer.querySelector(`[data-location-inventory="${locationId}"]`);
              if (!label) return;
              const isAvailable = store.quantityAvailable > 0;
              label.classList.toggle('is-available', isAvailable);
              label.classList.toggle('is-unavailable', !isAvailable);
              const indicator = label.querySelector('.status-indicator');
              indicator.classList.toggle('status-indicator--available', isAvailable);
              indicator.classList.toggle('status-indicator--unavailable', !isAvailable);
              const message = label.querySelector('[data-location-inventory-message]');
              if (store.quantityAvailable > 0) {
                const unit = document.createElement('strong');
                unit.textContent = drawer.dataset.quantityMessage.replaceAll('__pickup_quantity__', String(store.quantityAvailable));
                const parts = drawer.dataset.availableMessage.split('__pickup_quantity__');
                message.replaceChildren(parts[0]);
                parts.slice(1).forEach((part) => message.append(unit.cloneNode(true), part));
              } else {
                message.textContent = drawer.dataset.unavailableMessage;
              }
              label.hidden = false;
            });

            const nextCursor = availability.pageInfo.endCursor;
            if (!availability.pageInfo.hasNextPage || !nextCursor || nextCursor === after) break;
            after = nextCursor;
          } while (requestId === this.requestId && drawer.isConnected);
        } catch {
          // Keep Shopify's pickup availability and times if inventory cannot be loaded.
        }
      }

      update(variant) {
        if (variant?.id) {
          return this.fetchAvailability(variant.id);
        }
        ++this.requestId;
        this.clearDrawer();
        this.setAlternateButton(true);
        this.removeAttribute('available');
        this.innerHTML = '';
      }

      renderError() {
        this.clearDrawer();
        this.replaceChildren(this.errorHtml.cloneNode(true));
        this.querySelector('button').addEventListener('click', this.onClickRefreshList);
      }

      renderPreview(sectionInnerHTML) {
        this.clearDrawer();
        const preview = sectionInnerHTML?.querySelector('pickup-availability-preview');
        const drawer = sectionInnerHTML?.querySelector('pickup-availability-drawer');
        if (!preview || !drawer) {
          this.renderError();
          return;
        }

        this.innerHTML = preview.outerHTML;
        this.setAttribute('available', '');
        document.body.appendChild(drawer);
        const colorClassesToApply = (this.dataset.productPageColorScheme || '').split(' ').filter(Boolean);
        colorClassesToApply.forEach((colorClass) => drawer.classList.add(colorClass));

        this.querySelector('button')?.addEventListener('click', this.onClickShowDrawer);
        return drawer;
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);

        this.querySelector('button').addEventListener('click', () => {
          this.hide();
        });

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (
          target != this &&
          !target.closest('pickup-availability-drawer') &&
          target.id != 'ShowPickupAvailabilityDrawer'
        ) {
          if (target.id != 'ShowPickupAvailabilityDrawerAlternate') {
            this.hide();
          }
        }
      }

      hide() {
        this.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.focusElement);
      }

      show(focusElement) {
        this.focusElement = focusElement;
        this.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
        document.body.classList.add('overflow-hidden');
        trapFocus(this);
      }
    }
  );
}
